// Azure Application Insights - uncomment when ready to use
// import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

/* eslint-disable no-console */

type LogApi = {
  message: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    Log: LogApi;
  }

  const Log: LogApi;
}

type Environment = 'local' | 'dev' | 'test' | 'prod';

/**
 * LoggingService provides a centralized logging abstraction.
 * - In local/dev/test environments: logs to console only
 * - In prod environment: logs to both console and Azure Application Insights
 *
 * To enable Azure Application Insights:
 * 1. Install: npm install @microsoft/applicationinsights-web
 * 2. Uncomment the import at the top of this file
 * 3. Uncomment the appInsights property and initialization code
 * 4. Set the connection string in your environment config
 */
export class LoggingService {
  private constructor() {}

  private static environment: Environment = 'local';
  private static initialized = false;

  // Azure Application Insights instance - uncomment when ready
  // private static appInsights: ApplicationInsights | null = null;

  /**
   * Initialize the logging service with environment and optional Azure config.
   * Call this once at app startup (e.g., in main.ts or app.config.ts).
   */
  static initialize(env: Environment, _azureConnectionString?: string): void {
    this.environment = env;
    this.initialized = true;

    // Initialize Azure Application Insights for production
    // Uncomment the following block when ready to use Azure logging:
    /*
    if (this.isProd() && _azureConnectionString) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: _azureConnectionString,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
        },
      });
      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView();
      this.info('Azure Application Insights initialized');
    }
    */

    this.info(`LoggingService initialized for environment: ${env}`);
  }

  /**
   * Check if running in production environment.
   */
  private static isProd(): boolean {
    return this.environment === 'prod';
  }

  /**
   * Check if running in a non-production environment (local, dev, test).
   */
  private static isNonProd(): boolean {
    return ['local', 'dev', 'test'].includes(this.environment);
  }

  /**
   * Format arguments into a single message string for Azure logging.
   */
  private static formatMessage(args: unknown[]): string {
    return args.map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg))).join(' ');
  }

  /**
   * Log a general message.
   */
  static log(...args: unknown[]): void {
    console.log(...args);

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.trackTrace({
    //     message: this.formatMessage(args),
    //     severityLevel: SeverityLevel.Information,
    //   });
    // }
  }

  /**
   * Log an error message.
   */
  static error(...args: unknown[]): void {
    console.error(...args);

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   const error = args[0] instanceof Error ? args[0] : new Error(this.formatMessage(args));
    //   this.appInsights.trackException({
    //     exception: error,
    //     severityLevel: SeverityLevel.Error,
    //   });
    // }
  }

  /**
   * Log a warning message.
   */
  static warn(...args: unknown[]): void {
    console.warn(...args);

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.trackTrace({
    //     message: this.formatMessage(args),
    //     severityLevel: SeverityLevel.Warning,
    //   });
    // }
  }

  /**
   * Log an info message.
   */
  static info(...args: unknown[]): void {
    console.info(...args);

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.trackTrace({
    //     message: this.formatMessage(args),
    //     severityLevel: SeverityLevel.Information,
    //   });
    // }
  }

  /**
   * Log a debug message. Only logs to console in non-prod environments.
   */
  static debug(...args: unknown[]): void {
    // Debug logs only go to console and only in non-prod environments
    if (this.isNonProd()) {
      console.debug(...args);
    }

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.trackTrace({
    //     message: this.formatMessage(args),
    //     severityLevel: SeverityLevel.Verbose,
    //   });
    // }
  }

  /**
   * Track a custom event (useful for analytics).
   */
  static trackEvent(name: string, properties?: Record<string, string>): void {
    if (this.isNonProd()) {
      console.log(`[Event] ${name}`, properties);
    }

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.trackEvent({ name, properties });
    // }
  }

  /**
   * Track a page view.
   */
  static trackPageView(name: string, uri?: string): void {
    if (this.isNonProd()) {
      console.log(`[PageView] ${name}`, uri);
    }

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.trackPageView({ name, uri });
    // }
  }

  /**
   * Set the authenticated user context for Azure logging.
   */
  static setUser(userId: string, accountId?: string): void {
    if (this.isNonProd()) {
      console.log(`[User] ${userId}`, accountId);
    }

    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
    // }
  }

  /**
   * Clear the authenticated user context.
   */
  static clearUser(): void {
    // Azure logging for production - uncomment when ready:
    // if (this.isProd() && this.appInsights) {
    //   this.appInsights.clearAuthenticatedUserContext();
    // }
  }
}
